import { Link } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import { Button, Select } from '@nyce/admin-kit/ui';
import {
  ConfirmButton,
  EmptyState,
  ErrorBox,
  Loading,
  TableScroller,
  useAsync,
} from '../../../components/common';
import { formatDateTime } from '../../../lib/format';
import { monitorsFor, type BuddySystemRef } from '../../api/monitors';
import { buddyPath } from '../../shared';
import { AlertDialog } from './AlertDialog';
import { alertFinding } from './form';
import { ToneChip, useQueryState } from './parts';

/**
 * Alerts of one monitor (detail tab) or of all monitors (the overview on the list page). Filters and the open alert
 * live in the URL: `status`, `system`, `monitor` (overview only), `alert`. buddy3's all-monitor route only knows
 * `open` and `resolved`; the per-monitor one also lists both together.
 */
export function AlertsPanel({
  target,
  monitorId,
  monitorNames,
  systems,
  canWrite,
  notify,
}: {
  target: string;
  /** One monitor; absent = every monitor. */
  monitorId?: string;
  /** id → name, to label the overview's rows. */
  monitorNames?: Map<string, string>;
  systems: BuddySystemRef[];
  canWrite: boolean;
  notify: { success: (s: string) => void; error: (s: string) => void };
}) {
  const q = useQueryState();
  const status = q.get('status') || 'open';
  const systemId = q.get('system');
  const filterMonitor = monitorId ?? q.get('monitor');
  const api = monitorsFor(target);
  const alerts = useAsync(
    () =>
      api.alerts({
        monitorId: filterMonitor || undefined,
        status: status === 'all' ? undefined : status,
        systemId: systemId || undefined,
        limit: 500,
      }),
    [target, filterMonitor, status, systemId]
  );
  const items = alerts.data ?? [];
  const open = items.find((a) => a.id === q.get('alert'));
  const clear = async () => {
    try {
      const r = await api.clearAlerts(monitorId!, systemId || undefined);
      notify.success(`Resolved ${r.resolved} alert(s).`);
      await alerts.reload();
    } catch (e) {
      notify.error((e as Error).message);
    }
  };
  const statuses = [
    { value: 'open', label: 'Open' },
    { value: 'resolved', label: 'Resolved' },
    ...(monitorId ? [{ value: 'all', label: 'All' }] : []),
  ];
  return (
    <section className="nyk-card" data-testid="alerts-panel">
      <div className="nyk-card-head">
        <h2>{monitorId ? 'Alerts' : 'Alerts of all monitors'}</h2>
        <div className="bd-toolbar">
          <Select
            aria-label="Alert status"
            value={status}
            onChange={(v) => q.set({ status: v === 'open' ? null : v, alert: null })}
            options={statuses}
          />
          <Select
            aria-label="Alert system"
            value={systemId}
            onChange={(v) => q.set({ system: v, alert: null })}
            options={[
              { value: '', label: 'All systems' },
              ...systems.map((s) => ({ value: s.id, label: s.name })),
            ]}
          />
          {!monitorId && monitorNames && (
            <Select
              aria-label="Alert monitor"
              value={filterMonitor}
              onChange={(v) => q.set({ monitor: v, alert: null })}
              options={[
                { value: '', label: 'All monitors' },
                ...[...monitorNames].map(([id, name]) => ({ value: id, label: name })),
              ]}
            />
          )}
          <Button
            size="sm"
            variant="ghost"
            icon={<RefreshCw size={14} />}
            loading={alerts.loading}
            onClick={() => void alerts.reload()}
          >
            Refresh
          </Button>
          {monitorId && canWrite && (
            <ConfirmButton
              size="sm"
              variant="danger"
              message={`Resolve every open alert of this monitor${systemId ? ` on ${systemId}` : ''}? The next run opens again what is still there.`}
              onConfirm={() => void clear()}
            >
              Clear open alerts
            </ConfirmButton>
          )}
        </div>
      </div>
      <ErrorBox error={alerts.error} />
      <Loading loading={alerts.loading && !alerts.data} />
      {alerts.data && !items.length && (
        <EmptyState title={status === 'open' ? 'No open alerts.' : 'No alerts.'} />
      )}
      {items.length > 0 && (
        <TableScroller>
          <table className="nyk-ui-table nyk-cards" data-testid="alerts-table">
            <thead>
              <tr>
                <th>Entity</th>
                {!monitorId && <th>Monitor</th>}
                <th>System</th>
                <th>Status</th>
                <th>Finding</th>
                <th>Opened</th>
                <th>Last seen</th>
              </tr>
            </thead>
            <tbody>
              {items.map((a) => {
                const f = alertFinding(a);
                return (
                  <tr key={a.id} data-alert={a.id}>
                    <td className="nyk-card-title">
                      <button type="button" className="nyk-link" onClick={() => q.set({ alert: a.id })}>
                        <code>{a.entityKey}</code>
                      </button>
                    </td>
                    {!monitorId && (
                      <td data-label="Monitor">
                        <Link to={buddyPath(`/buddy/monitors/${encodeURIComponent(a.monitorId)}`, target)}>
                          {monitorNames?.get(a.monitorId) ?? a.monitorId}
                        </Link>
                      </td>
                    )}
                    <td data-label="System">{a.systemId}</td>
                    <td data-label="Status">
                      <ToneChip kind="alert" value={a.status} />
                    </td>
                    <td data-label="Finding">
                      <span className="nyk-inline">
                        <ToneChip kind="severity" value={f.severity} />
                        <span className="bm-clip">
                          {f.title ?? f.summary ?? <span className="nyk-ui-hint">no finding yet</span>}
                        </span>
                      </span>
                    </td>
                    <td data-label="Opened">{formatDateTime(a.openedAt)}</td>
                    <td data-label="Last seen">{formatDateTime(a.lastSeenAt)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </TableScroller>
      )}
      {open && (
        <AlertDialog
          target={target}
          alert={open}
          monitorName={monitorId ? undefined : monitorNames?.get(open.monitorId)}
          canWrite={canWrite}
          onClose={() => q.set({ alert: null })}
        />
      )}
    </section>
  );
}
